//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { TUniversalDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { TUniversalScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';

// These are all the different types of posts we need to handle on the Timeline and Calendar
export enum UniversalPostType {
  FacebookPageFeedPost = 'FacebookPageFeedPost',
  TikTokVideoAutomaticPost = 'TikTokVideoAutomaticPost',
  PinterestOrganicPin = 'PinterestOrganicPin',
  TwitterMediaPost = 'TwitterMediaPost',
  InstagramFeedAutomaticPost = 'InstagramFeedAutomaticPost',
  InstagramFeedManualPost = 'InstagramFeedManualPost',
  InstagramReelsAutomaticPost = 'InstagramReelsAutomaticPost',
  InstagramReelsManualPost = 'InstagramReelsManualPost',
  InstagramStoryGroupAutomaticPost = 'InstagramStoryGroupAutomaticPost',
  InstagramStoryGroupPost = 'InstagramStoryGroupPost',
  YouTubeAutomaticPost = 'YouTubeAutomaticPost',
}

export const AllUniversalPostTypes = Object.values(UniversalPostType);

export const UniversalPostTypeValues = Object.values(UniversalPostType);

export type InstagramUniversalPostType =
  | UniversalPostType.InstagramFeedAutomaticPost
  | UniversalPostType.InstagramFeedManualPost
  | UniversalPostType.InstagramReelsAutomaticPost
  | UniversalPostType.InstagramReelsManualPost
  | UniversalPostType.InstagramStoryGroupAutomaticPost
  | UniversalPostType.InstagramStoryGroupPost;

export const isInstagramUniversalPostType = (
  postType: UniversalPostType,
): postType is InstagramUniversalPostType => {
  return [
    UniversalPostType.InstagramFeedAutomaticPost,
    UniversalPostType.InstagramFeedManualPost,
    UniversalPostType.InstagramReelsAutomaticPost,
    UniversalPostType.InstagramReelsManualPost,
    UniversalPostType.InstagramStoryGroupAutomaticPost,
    UniversalPostType.InstagramStoryGroupPost,
  ].includes(postType);
};

export type TUniversalPostItem = TUniversalScheduledPost | TUniversalDraftPost;

export const UniversalPostingGroupName = {
  FacebookPageFeedGroup: 'facebook-page-feed',
  InstagramFeedAutomaticGroup: 'instagram-automatic-post-group',
  InstagramFeedManualGroup: 'instagram-manual-post-group',
  InstagramReelsAutomaticGroup: 'instagram-reels-automatic-posting-group',
  InstagramReelsManualGroup: 'instagram-reels-posting-group',
  InstagramStoryGroupAutomaticPostingGroup:
    'instagram-story-group-automatic-posting-group',
  InstagramStoryGroupPostingGroup: 'instagram-story-group-posting-group',
  PinterestOrganicPinGroup: 'pinterest-organic-pin-group',
  TikTokVideoAutomaticGroup: 'tiktok-video-automatic-posting-group',
  TwitterPostingGroup: 'twitter-posting-group',
  YouTubeAutomaticPostingGroup: 'youtube-automatic-posting-group',
} as const;

export type UniversalPostingGroupName =
  (typeof UniversalPostingGroupName)[keyof typeof UniversalPostingGroupName];

export const allUniversalPostingGroupNames = Object.values(
  UniversalPostingGroupName,
);
export const facebookPostingGroupNames = [
  UniversalPostingGroupName.FacebookPageFeedGroup,
];
export const instagramPostingGroupNames = [
  UniversalPostingGroupName.InstagramFeedAutomaticGroup,
  UniversalPostingGroupName.InstagramFeedManualGroup,
  UniversalPostingGroupName.InstagramReelsAutomaticGroup,
  UniversalPostingGroupName.InstagramReelsManualGroup,
  UniversalPostingGroupName.InstagramStoryGroupAutomaticPostingGroup,
  UniversalPostingGroupName.InstagramStoryGroupPostingGroup,
];
export const instagramStoryPostingGroupNames = [
  UniversalPostingGroupName.InstagramStoryGroupAutomaticPostingGroup,
  UniversalPostingGroupName.InstagramStoryGroupPostingGroup,
];
export const pinterestPostingGroupNames = [
  UniversalPostingGroupName.PinterestOrganicPinGroup,
];
export const tiktokPostingGroupNames = [
  UniversalPostingGroupName.TikTokVideoAutomaticGroup,
];
export const twitterPostingGroupNames = [
  UniversalPostingGroupName.TwitterPostingGroup,
];
export const youtubePostingGroupNames = [
  UniversalPostingGroupName.YouTubeAutomaticPostingGroup,
];
export const manualPostingGroupNames = [
  UniversalPostingGroupName.InstagramFeedManualGroup,
  UniversalPostingGroupName.InstagramReelsManualGroup,
  UniversalPostingGroupName.InstagramStoryGroupPostingGroup,
];
